import type { IntranetCategory } from '@gb-ticket/shared-types';
import {
  BookmarkIcon,
  Building2Icon,
  CircleIcon,
  NewspaperIcon,
  SearchIcon,
  UsersIcon,
  WrenchIcon,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';
import { IntranetShowMore } from '@/products/intranet/components/intranet-show-more';
import { visibleIntranetChannels } from '@/products/intranet/features/intranet/channels';
import { coverSrc } from '@/products/intranet/features/intranet/cover-src';
import { INTRANET_ROOT } from '@/products/intranet/intranet-paths';

export type IntranetChannelListProps = {
  categories: readonly IntranetCategory[];
  /** Active rubric slug from `?category=`. Empty when „Alle“ or „Gespeichert“ is active. */
  activeSlug: string | null;
  saved: boolean;
  /** Number of own saved posts, when the count query has answered. */
  savedCount?: number;
  onPick?: () => void;
};

export function IntranetChannelList({
  categories,
  activeSlug,
  saved,
  savedCount,
  onPick,
}: IntranetChannelListProps) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const { items, collapsed, canCollapse } = visibleIntranetChannels(categories, query, expanded);

  return (
    <nav
      aria-label="Meine Channels"
      className="space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <h2 className="px-1 text-sm font-semibold text-primary">Meine Channels</h2>
      <label className="relative block">
        <span className="sr-only">Channels durchsuchen</span>
        <SearchIcon
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Suchen …"
          className="h-11 w-full rounded-lg border bg-background pr-3 pl-9 text-sm focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
        />
      </label>
      <ul className="space-y-1">
        <li>
          <ChannelLink to={INTRANET_ROOT} active={!activeSlug && !saved} onPick={onPick}>
            <CircleIcon className="size-4 shrink-0" aria-hidden />
            Alle
          </ChannelLink>
        </li>
        {items.map((category) => (
          <li key={category.id}>
            <ChannelLink
              to={`${INTRANET_ROOT}?category=${encodeURIComponent(category.slug)}`}
              active={activeSlug === category.slug}
              onPick={onPick}
            >
              <ChannelMark category={category} />
              {category.name}
            </ChannelLink>
          </li>
        ))}
      </ul>
      {collapsed ? (
        <IntranetShowMore label="Mehr anzeigen" onClick={() => setExpanded(true)} />
      ) : null}
      {canCollapse ? (
        <IntranetShowMore label="Weniger anzeigen" onClick={() => setExpanded(false)} />
      ) : null}
      <ChannelLink
        to={`${INTRANET_ROOT}?saved=true`}
        active={saved}
        onPick={onPick}
        className="mt-2"
      >
        <BookmarkIcon className="size-4 shrink-0" aria-hidden />
        Gespeicherte Beiträge
        {savedCount === undefined ? null : (
          <span className="ml-auto text-xs tabular-nums">{savedCount}</span>
        )}
      </ChannelLink>
    </nav>
  );
}

/** Lucide component names stored on a rubric. Unknown names fall back to a dot. */
const CHANNEL_ICONS: Record<string, LucideIcon> = {
  Bookmark: BookmarkIcon,
  Building2: Building2Icon,
  Newspaper: NewspaperIcon,
  Users: UsersIcon,
  Wrench: WrenchIcon,
};

export function ChannelIcon({ name }: { name: string | null }) {
  const Icon = name ? CHANNEL_ICONS[name] : undefined;
  if (!Icon) {
    return <CircleIcon className="size-4 shrink-0" aria-hidden />;
  }
  return <Icon className="size-4 shrink-0" aria-hidden />;
}

function ChannelMark({ category }: { category: IntranetCategory }) {
  const src = category.imageUrl ? coverSrc({ coverUrl: category.imageUrl }) : null;
  if (src) {
    return <img src={src} alt="" className="size-4 shrink-0 rounded-sm object-cover" />;
  }
  return <ChannelIcon name={category.iconName} />;
}

function ChannelLink({
  to,
  active,
  onPick,
  className,
  children,
}: {
  to: string;
  active: boolean;
  onPick?: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'true' : undefined}
      onClick={onPick}
      className={cn(
        'flex min-h-11 items-center gap-2 rounded-lg border-l-4 px-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none',
        active
          ? 'border-brand-navy bg-brand-navy-soft text-brand-navy'
          : 'border-transparent text-foreground hover:bg-muted',
        className,
      )}
    >
      {children}
    </Link>
  );
}
